import { useEffect, useState, useContext } from "react";
import "./AbTestStyles.css";
import AbTestDataBox from "./AbTestDataBox";
import { SelectedProjectContext } from "../../contexts";
import { createAbTest } from "../../helpers";
import CustomDropdown from "../CustomDropdown/CustomDropdown";
import CustomInput from "../CustomInput/CustomInput";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";

const AbTestStep3 = ({
  setActiveTab,
  selectedTest,
  weeklySessions,
  hasWeekOfData,
  setSelectedTest,
  goals,
}) => {
  const selectedProject = useContext(SelectedProjectContext);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [goalsDropdownOpen, setGoalsDropdownOpen] = useState(false);

  const [savingTest, setSavingTest] = useState(false);

  const [title, setTitle] = useState("");
  const [aTraffic, setATraffic] = useState(50);
  const [bTraffic, setBTraffic] = useState(50);
  const [estimateMessage, setEstimateMessage] = useState("");
  const [estimate, setEstimate] = useState(0);

  const [previousGoal, setPreviousGoal] = useState(null);

  useEffect(() => {
    if (selectedTest.title) setTitle(selectedTest.title);
    if (selectedTest.percentage !== undefined) {
      setATraffic(100 - selectedTest.percentage);
      setBTraffic(selectedTest.percentage);
    }

    if (goals) {
      const goal = goals.find((goal) => goal.id === selectedTest.goalId);
      if (goal) {
        setSelectedGoal({ label: goal.label, value: goal.id });
        setPreviousGoal(goal.label);
      }
    }
  }, []);

  useEffect(() => {
    if (hasWeekOfData && weeklySessions && bTraffic && bTraffic !== "0") {
      const dailySessions = Math.round(weeklySessions / 7);
      const estimate = Math.round(
        2000 / Math.round(weeklySessions / 7) / (bTraffic / 100),
      );
      setEstimate(estimate);
      setEstimateMessage(
        `Your website is generating around ${dailySessions} visitors / day. If you send ${bTraffic}% of the traffic to the new version, we estimate you will need ${estimate} days to have valuable metrics. `,
      );
    } else if (bTraffic === "0") {
      setEstimate(0);
      setEstimateMessage(
        "You are not sending any traffic to the new version. This means the test will never complete. ",
      );
    } else {
      setEstimate(null);
      setEstimateMessage("");
    }
  }, [hasWeekOfData, weeklySessions, bTraffic]);

  function handleATrafficChange(e) {
    let raw = e.target.value.replace(/[^0-9.]/g, "");
    if (raw === "") {
      setATraffic("");
      setBTraffic("");
      return;
    }

    const value = parseInt(raw, 10);
    if (value >= 0 && value <= 100) {
      setATraffic(value.toString());
      setBTraffic((100 - value).toString());
    }
  }

  function handleBTrafficChange(e) {
    let raw = e.target.value.replace(/[^0-9.]/g, "");
    if (raw === "") {
      setATraffic("");
      setBTraffic("");
      return;
    }

    const value = parseInt(raw, 10);
    if (value >= 0 && value <= 100) {
      setBTraffic(value.toString());
      setATraffic((100 - value).toString());
    }
  }

  async function handleTestCreation() {
    setSavingTest(!savingTest);
    await createAbTest(
      selectedProject.value,
      selectedProject.matomoId,
      title,
      bTraffic,
      selectedTest.url ?? selectedProject.rootUrl,
      selectedTest.data,
      selectedTest.jsSelector,
      selectedTest.type,
      selectedGoal.value,
      selectedTest.screenshotOriginal,
      selectedTest.device,
    ).then((res) => {
      if (res) {
        setSelectedTest(res);
        setActiveTab(4);
      } else {
        setSavingTest(false);
        toast.error(
          "There was an error creating the test. Please contact an admin.",
        );
      }
    });
  }

  return (
    <div>
      <div className="mb-100 abtest-w367">
        <div className="abtest-info-block mb-50 w-min">
          <div>SetUp your test.</div>
          <div>Users with slow internet will be excluded from A/B testing</div>
        </div>
        <div className="fw-700 fs-18 fc-black mb-20">Define the goal</div>

        <div className="no-margins-or-padding mb-50">
          <div className="mb-16">
            <CustomDropdown
              unstyled
              placeholder="Goal"
              options={goals.map((goal) => ({
                label: goal.label,
                value: goal.id,
              }))}
              onChange={(e) => {
                setSelectedGoal(e);
              }}
              value={selectedGoal}
              menuOpenState={goalsDropdownOpen}
              setMenuOpenState={setGoalsDropdownOpen}
            />
          </div>
          <div className="fc-grey fw-500 fs-16 lh-20 mb-20">
            A user action that defines success, like a click, signup, or
            purchase, used to measure test performance.
          </div>
          {previousGoal && (
            <div>
              <AbTestDataBox
                type="grey"
                header="Target goal"
                content={previousGoal}
              />
            </div>
          )}
        </div>

        <div>
          <div className="fw-700 fs-18 fc-black mb-5">Set up the test</div>
          <div className="mb-10">
            <CustomInput
              minWidth="100%"
              type="text"
              placeholder={"Name of your test"}
              onChange={(e) => setTitle(e.target.value)}
              valueState={title}
            />
            <CustomInput
              minWidth="100%"
              type="text"
              isPercent
              placeholder={"Traffic on the current version (A)"}
              onChange={(e) => {
                handleATrafficChange(e);
              }}
              valueState={aTraffic}
            />
            <CustomInput
              minWidth="100%"
              type="text"
              isPercent
              placeholder={"Traffic to the new version (B)"}
              onChange={(e) => {
                handleBTrafficChange(e);
              }}
              valueState={bTraffic}
            />
          </div>
          <div className="mb-16">
            <AbTestDataBox
              type="grey2"
              header="Time to receive results"
              content={
                hasWeekOfData && estimate && bTraffic !== "0"
                  ? `${estimate} days`
                  : "Not enough data to estimate!"
              }
            />
          </div>

          <div className="fc-grey fw-500 fs-16 lh-20 mb-50">
            {estimateMessage}
            Don’t hesitate to increase the traffic on the new version.
          </div>

          <div className="d-flex">
            <button
              className="btn-start-the-test btn-primary fw-700 mr-16 vhc"
              disabled={
                !aTraffic || !bTraffic || !title || !selectedGoal || savingTest
              }
              onClick={handleTestCreation}
            >
              {savingTest ? <Spinner></Spinner> : <>Start the test</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AbTestStep3;
